import {Box, Checkbox, FormControlLabel, IconButton, Stack, Typography} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import React, {ReactNode, useMemo} from "react";
import {NewJoinerChecklistItem} from "../../api/Model";

/**
 * A checklist row with an optional body (shown below the label)
 * and optional sub-items (shown indented with a left border).
 * When `dimmed`, the row (including body and sub-items) is shown at reduced opacity.
 */
export function ChecklistSection({checked, intermediate, readOnly = false, disabled = false, body, children}: {
    checked: boolean;
    intermediate?: boolean;
    readOnly?: boolean;
    disabled?: boolean;
    body: React.ReactNode;
    children?: React.ReactNode;
}) {
    return (
        <Box>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={checked}
                        indeterminate={intermediate}
                        readOnly={readOnly}
                        sx={{px: 1, py: 0, pointerEvents: readOnly ? 'none' : 'auto'}}
                    />
                }
                label={body}
                disabled={disabled}
                color="primary"
                slotProps={{
                    typography: {
                        component: 'div',
                    }
                }}
                sx={{width: '100%', display: 'flex', alignItems: 'center', '& .MuiFormControlLabel-label': {flexGrow: 1, overflow: 'auto'}}}
            />
            {children && (
                <Stack direction="column" sx={{ml: 1, pl: 2, mt: 0.25, borderLeft: '2px solid', borderColor: 'divider'}}>
                    {children}
                </Stack>
            )}
        </Box>
    );
}

export function ChecklistGroup({headerChecked, disabled = false, children, nestedToggles}: {
    headerChecked?: boolean,
    disabled?: boolean,
    nestedToggles: {
        item: NewJoinerChecklistItem;
        checked: boolean;
        disabled?: boolean;
        toggle: () => any;
    }[],
    children: ReactNode,
}) {
    const itemCount = useMemo(
        () => ({
            total: (headerChecked !== undefined ? 1 : 0) + nestedToggles.length,
            checked: (headerChecked ? 1 : 0) + nestedToggles
                .filter(toggle => toggle.checked)
                .length,
        }),
        [headerChecked, nestedToggles],
    );

    const subItemsDisabled = disabled || (headerChecked !== undefined && !headerChecked);

    return (
        <ChecklistSection
            checked={itemCount.checked === itemCount.total}
            intermediate={headerChecked !== false && itemCount.checked > 0 && itemCount.checked < itemCount.total}
            readOnly={true}
            disabled={disabled}
            body={children}
        >
            {nestedToggles.map(toggle => (
                <ManualSubCheckItem
                    key={toggle.item.id}
                    label={toggle.item.label}
                    externalLink={toggle.item.link}
                    checked={!subItemsDisabled && toggle.checked}
                    disabled={subItemsDisabled || toggle.disabled}
                    onClick={toggle.toggle}
                />
            ))}
        </ChecklistSection>
    );
}

export function SubCheckItem({checked, children, onClick, disabled = false}: {
    checked: boolean,
    children: ReactNode,
    onClick?: () => void,
    disabled?: boolean
}) {
    return (
        <FormControlLabel
            control={<Checkbox checked={checked} onChange={onClick} sx={{px: 1, py: 0}}/>}
            label={children}
            disabled={disabled}
        />
    );
}

export function ManualSubCheckItem({checked, onClick, label, externalLink, disabled = false}: {
    checked: boolean,
    onClick?: () => any,
    label: string,
    externalLink?: string | null,
    disabled?: boolean
}) {
    return (
        <SubCheckItem checked={checked} onClick={onClick} disabled={disabled}>
            <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="body2">
                    {label}
                </Typography>

                {externalLink && (
                    <IconButton
                        component="a"
                        href={externalLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        sx={{width: 24, height: 24}}
                    >
                        <OpenInNewIcon sx={{fontSize: 16}}/>
                    </IconButton>
                )}
            </Stack>
        </SubCheckItem>
    );
}
